import { Text } from "@hackthenorth/north";
import { Button } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { PageWrapper, Icon } from "src/shared/components";
import Header from "src/shared/components/Header";
import ScheduleToggle from "src/views/schedule/components/ScheduleToggle";
import { SCHEDULE_VIEW_TYPE } from "src/views/schedule/constants";

import MentorShiftModal from "../../shared/components/MentorShiftModal";
import { useMentorShiftsQuery } from "../../shifts/graphql/mentorShifts.generated";
import { TMentorShiftSchedule } from "../types";
import { parseAdminShifts, handleDownloadCSV } from "../utils";

import AdminShiftCalendarView from "./AdminShiftCalendarView";
import AdminShiftListView from "./AdminShiftListView";

export const AdminShiftSchedule = () => {
  const [currentView, setCurrentView] = useState<SCHEDULE_VIEW_TYPE>(
    SCHEDULE_VIEW_TYPE.CALENDAR_VIEW
  );

  const [selectedShift, setSelectedShift] = React.useState<number | null>(null);
  const [viewModal, setViewModal] = React.useState<boolean>(false);

  useEffect(() => {
    if (viewModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [viewModal]);

  const {
    data: mentorShiftsData,
    loading: fetchingMentorShiftsData,
    refetch: refetchMentorShifts,
  } = useMentorShiftsQuery({ variables: { where: {} }, pollInterval: 10000 });

  const [adminShifts, setAdminShifts] = useState<TMentorShiftSchedule[]>([]);

  useEffect(() => {
    if (mentorShiftsData && !fetchingMentorShiftsData) {
      const newShifts = parseAdminShifts(mentorShiftsData);

      // Sort the shifts by date and then by time_start
      const sortedShifts = newShifts.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA.getTime() - dateB.getTime();
        }
        return a.time_start.getTime() - b.time_start.getTime();
      });

      setAdminShifts(sortedShifts);
    }
  }, [mentorShiftsData, fetchingMentorShiftsData]);

  const toggleView = () => {
    setCurrentView((currentView) => {
      if (currentView === SCHEDULE_VIEW_TYPE.LIST_VIEW)
        return SCHEDULE_VIEW_TYPE.CALENDAR_VIEW;

      return SCHEDULE_VIEW_TYPE.LIST_VIEW;
    });
  };

  return (
    <>
      {selectedShift && (
        <MentorShiftModal
          shiftId={selectedShift}
          open={viewModal}
          setOpen={setViewModal}
          onChange={() => {
            refetchMentorShifts();
          }}
        />
      )}
      <Header
        title="Shift Schedule"
        subtitle="Register for your mentor shifts"
        actionButton={
          <Button
            leadingIcon={
              <Icon name="spon-download" size="14px" color="transparent" />
            }
            color="primary"
            onClick={() =>
              handleDownloadCSV(adminShifts, "admin_mentor_shift_schedule")
            }
          >
            Download CSV
          </Button>
        }
      />

      <PageWrapper>
        <HeaderFlex>
          <Text mods="heading h3">Schedule</Text>
          <ScheduleToggle
            currentView={currentView}
            onToggle={() => toggleView()}
          ></ScheduleToggle>
        </HeaderFlex>
        <div className="text-text-tertiary mb-4">
          All times are in Eastern Time (EDT)
        </div>
        <div style={{ width: "100%", paddingTop: "32px" }}>
          {currentView === SCHEDULE_VIEW_TYPE.LIST_VIEW && (
            <AdminShiftListView
              fetchingShifts={fetchingMentorShiftsData}
              adminShifts={adminShifts}
              setSelectedShift={setSelectedShift}
              setViewModal={setViewModal}
            />
          )}
          {currentView === SCHEDULE_VIEW_TYPE.CALENDAR_VIEW && (
            <>
              <AdminShiftCalendarView
                fetchingShifts={fetchingMentorShiftsData}
                adminShifts={adminShifts}
                setSelectedShift={setSelectedShift}
                setViewModal={setViewModal}
              />
            </>
          )}
        </div>
      </PageWrapper>
    </>
  );
};

const HeaderFlex = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
